import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { SeasonBadge } from './Badges';
import { displayTitle } from './format';
import {
  ACCOUNTING_BROKEN, accountingAddsUp, accountingLine, accountingOf, bucketStyles, emptyBuckets,
  filledBuckets, unaccountedLine,
} from './accounting';
import { C, MONO, RADIUS, SHADOW } from './tokens';
import { RestockV2AccountingBucket, RestockV2Plan } from './types';

// 「清單 73 款：37 要訂、17 已經夠了、13 上架、3 帶回、2 不能訂、1 去拿」 — on the 訂貨 screen,
// always, without anyone being asked.
//
// Magnus asked the same question three times: 訂貨 also doesn't show all the items in the 清單 /
// why 訂貨 didn't show all the items in my 選品 but 門市 no stock / still why not showing all the
// 選品 item list items? Every time, the answer was a reconciliation someone ran in a terminal and
// pasted at him, and every time the data turned out to be complete and correct. 訂貨 draws the
// styles with pieces to order; the rest are hanging on the rail, in the office, already stocked,
// or blocked — all perfectly accounted for INSIDE the engine and nowhere at all on the screen. So
// he counted 73 on one tab, ~37 on the next, and had no way to close the gap except to ask.
//
// Two rules this block lives by:
//
// 1. IT ADDS UP OR IT SAYS NOTHING. A partition that double-counts, or whose parts miss the list
//    total, is strictly worse than no accounting: it sends him back to asking, holding a number
//    he has been told to trust. The numbers are the server's (noke-restock-v2-reconcile), summing
//    is checked before render, and a mismatch replaces the whole thing with one honest sentence.
//
// 2. EVERY COUNT OPENS. 「17 已經夠了」 that cannot be tapped is the same dead end wearing a new
//    shape. Each bucket is a button over the fold pattern 已排除 / 建議帶回 already use, and the
//    styles inside come from plan.list.approved — the very rows 選品 showed him.
//
// Empty buckets are drawn too, as quiet text. 「0 沒有下單連結」 is not noise: it is the line that
// says nothing is stuck. Dropping the zeros would leave a row that looks complete without being
// complete, which is the whole complaint again in miniature.

const Chip: React.FC<{
  bucket: RestockV2AccountingBucket; open: boolean; onToggle: () => void;
}> = ({ bucket, open, onToggle }) => (
  <Box
    component="button" type="button" onClick={onToggle} aria-expanded={open}
    aria-label={`${bucket.label} ${bucket.count} 款`}
    sx={{
      appearance: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px',
      border: '1px solid', borderColor: open ? C.ink : C.line2,
      bgcolor: open ? C.ink : C.card, color: open ? C.onInk : C.ink,
      borderRadius: '99px', px: 1.1, py: '5px', fontWeight: 800, fontSize: 12.5,
      whiteSpace: 'nowrap', flex: 'none',
    }}
  >
    {bucket.label}
    <Box
      component="span"
      sx={{
        fontFamily: MONO, fontSize: 13, fontWeight: 700,
        color: open ? C.onInk : C.ink2,
      }}
    >
      {bucket.count}
    </Box>
    <Box component="span" aria-hidden="true" sx={{ fontSize: 10, opacity: 0.75 }}>
      {open ? '▾' : '▸'}
    </Box>
  </Box>
);

const StyleRow: React.FC<{
  sku: string; title: string | null; season: React.ComponentProps<typeof SeasonBadge>['value'];
  onRail: boolean | null; laneReason: string | null;
}> = ({ sku, title, season, onRail, laneReason }) => (
  <Box
    data-accounting-sku={sku}
    sx={{
      display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.6, borderTop: '1px solid',
      borderColor: C.line, minWidth: 0,
    }}
  >
    <Typography
      component="span"
      sx={{
        fontFamily: MONO, fontSize: 11.5, fontWeight: 700, bgcolor: C.paper2, color: C.ink2,
        borderRadius: '3px', px: '4px', flex: 'none',
      }}
    >
      {sku}
    </Typography>
    <Typography
      component="span"
      sx={{
        fontSize: 12.5, fontWeight: 700, letterSpacing: '-.01em', minWidth: 0, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
    >
      {/* Not 「（讀不到）」 — the SKU beside it is already the honest statement that this style is
          on his list and the plan cannot name it. */}
      {title ? displayTitle(title) : '（清單找不到名稱）'}
    </Typography>
    <SeasonBadge value={season} size="xs" />
    {onRail === false && (
      <Typography component="span" sx={{ fontSize: 11, color: C.ink3, fontWeight: 700, flex: 'none' }}>
        不在櫃
      </Typography>
    )}
    {laneReason && (
      <Typography
        component="span"
        sx={{
          ml: 'auto', fontSize: 11.5, color: C.ink3, minWidth: 0, overflow: 'hidden',
          textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right',
        }}
      >
        {laneReason}
      </Typography>
    )}
  </Box>
);

const OrderAccounting: React.FC<{ plan: RestockV2Plan }> = ({ plan }) => {
  // One bucket open at a time: 37 rows plus 17 plus 13 stacked open is a wall, and the question
  // being answered is 「where did THOSE go」, one bucket at a time.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const order = plan.order;
  const accounting = accountingOf(plan);
  // Never conditional on a count: the coverage ledger's alarm is drawn whenever it fires, whether
  // or not there is a curated list to account for.
  const lost = unaccountedLine(order);
  if (!accounting && !lost) return null;

  const alarm = lost ? (
    <Box
      role="alert" aria-label="沒有進到任何清單"
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
        bgcolor: C.backWash, border: '2px solid', borderColor: C.back, borderRadius: '10px',
        px: 1.25, py: 0.9, mb: accounting ? 1.1 : 0,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 900, color: C.back }}>{lost}</Typography>
      <Typography sx={{ fontSize: 12, color: C.ink2 }}>
        這是系統的問題，不是你的操作。下面「這些沒有進到任何清單」有每一款的細節。
      </Typography>
    </Box>
  ) : null;

  if (!accounting) {
    return (
      <Box component="section" data-accounting="" sx={{ mb: 1.5 }}>{alarm}</Box>
    );
  }

  // The one case where showing the numbers would be worse than showing none.
  if (!accountingAddsUp(accounting)) {
    return (
      <Box component="section" data-accounting="broken" sx={{ mb: 1.5 }}>
        {alarm}
        <Typography
          role="alert"
          sx={{
            fontSize: 13, fontWeight: 800, color: C.back, bgcolor: C.backWash, border: '1px solid',
            borderColor: C.back, borderRadius: '10px', px: 1.25, py: 0.9,
          }}
        >
          {ACCOUNTING_BROKEN}
        </Typography>
      </Box>
    );
  }

  const filled = filledBuckets(accounting);
  // 「沒進清單 0」 is a true and reassuring line on a healthy plan — and a flat contradiction of
  // the alarm sitting directly above it when one is firing. Both statements are correct: the
  // buckets partition HIS LIST, while the ledger walks every style the plan touched, so a style
  // he never approved can be lost without moving that bucket off zero. Nobody should have to
  // work that out from two lines 20px apart, and the alarm is the one that must win, so the zero
  // steps aside while it is on screen. It is only ever suppressed at zero: a non-zero
  // 沒進到任何清單 is a chip in the row above and stays in the arithmetic.
  const empties = emptyBuckets(accounting).filter((bucket) => !(lost && bucket.key === 'unaccounted'));
  const open = filled.filter((bucket) => bucket.key === openKey)[0] || null;
  const rows = bucketStyles(plan, open);

  return (
    <Box
      component="section" data-accounting="" aria-label="選品清單去向"
      sx={{
        bgcolor: C.card, border: '1px solid', borderColor: C.line, borderRadius: `${RADIUS}px`,
        boxShadow: SHADOW, p: 1.1, mb: 1.5,
      }}
    >
      {alarm}
      {/* The sentence he can read without touching anything — the whole point of the block. */}
      <Typography sx={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '-.01em', mb: 0.8 }}>
        {accountingLine(accounting)}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
        {filled.map((bucket) => (
          <Chip
            key={bucket.key} bucket={bucket} open={bucket.key === openKey}
            onToggle={() => setOpenKey((old) => (old === bucket.key ? null : bucket.key))}
          />
        ))}
      </Box>
      {empties.length > 0 && (
        <Typography
          aria-label="沒有任何一款的分類"
          sx={{ fontSize: 11.5, color: C.ink3, mt: 0.7, lineHeight: 1.5 }}
        >
          {/* The SHORT word here, the full one on the chips. These are the quiet half of the
              arithmetic and there are seven of them: at 400px the full labels wrapped to two
              lines and cost the first order card a chunk of the fold for no extra meaning. */}
          {empties.map((bucket) => `${bucket.short} 0`).join(' · ')}
        </Typography>
      )}
      <Collapse in={!!open} unmountOnExit>
        {open && (
          <Box
            aria-label={`${open.label} ${open.count} 款`}
            sx={{
              mt: 0.9, bgcolor: C.wash, border: '1px solid', borderColor: C.line,
              borderRadius: '9px', maxHeight: 320, overflowY: 'auto',
            }}
          >
            {rows.map((row) => (
              <StyleRow
                key={`${open.key}:${row.sku}`} sku={row.sku} title={row.title} season={row.season}
                onRail={row.onRail} laneReason={row.laneReason}
              />
            ))}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default OrderAccounting;
